import clsx from "clsx";
import HeatmapDetails from "components/popover/HeatmapDetails";
import CustomTooltip from "components/popover/Tooltip";
import Typography from "components/Typography";
import { EncounterDetails } from "entities/issues";
import { useMemo } from "react";
import {
  ConfigVitalsToUnitMapper,
  EncounterWidth,
  VitalsTileHeight,
} from "utils/constants";
import {
  getGroupWidth,
  getTextColorBasedOnBg,
  getVitalsToDisplay,
} from "utils/helpers";

type VitalsTileProps = {
  config: { id: number; name: string };
  groups: string[];
  encounters: Record<string, EncounterDetails[]>;
  view: "year" | "month" | "day";
};

type VitalGroupProps = {
  group: string;
  groups: string[];
  encounters: Record<string, EncounterDetails[]>;
  view: "year" | "month" | "day";
  config: { id: number; name: string };
  index: number;
};
const VitalGroup = (props: VitalGroupProps) => {
  const { group, encounters, view, config, index, groups } = props;

  const encounterToShow = useMemo(() => {
    if (view == "year") return encounters[group].slice(0, 4);
    return encounters[group];
  }, [encounters, group, view]);

  return (
    <div
      className="border-y flex"
      style={{
        width:
          view == "year"
            ? `${Object.keys(encounters).length * 500}px`
            : view == "month"
            ? getGroupWidth(encounters, group)
            : encounters[group].length * EncounterWidth + 300,
        gap: view == "month" ? "24px" : "0",
      }}
    >
      {index == groups.length - 1 ? (
        <div className="flex items-center justify-end w-full pr-2 line-clamp-1">
          {config?.name} {ConfigVitalsToUnitMapper[config?.name]}{" "}
        </div>
      ) : (
        <>
          {encounterToShow?.map((encounter, index) => {
            const values = getVitalsToDisplay(
              encounter.vitals[0],
              config?.name
            );
            const textColor = getTextColorBasedOnBg(
              values.background || "#b2beb5"
            );
            return (
              <div
                key={index}
                className={clsx(
                  `w-[${EncounterWidth}px] 
                  hover:cursor-pointer text-sm `,
                  {
                    "border-x": values.name,
                  }
                )}
                style={{
                  backgroundColor: values.background,
                }}
              >
                {values.name ? (
                  <CustomTooltip
                    followCursor
                    title={
                      <HeatmapDetails
                        date={encounter.date}
                        facility={encounter.facility}
                        value={values.name}
                      />
                    }
                  >
                    <div
                      className={`w-[${EncounterWidth}px] h-[60px] flex items-center justify-center`}
                      style={{
                        color: textColor,
                      }}
                    >
                      <Typography
                        weight="bold"
                        htmlTag="p"
                        type="inherit"
                        clamp="clamp1"
                      >
                        {values.name}
                      </Typography>
                    </div>
                  </CustomTooltip>
                ) : (
                  <div
                    className={`w-[${EncounterWidth}px] h-[60px] flex items-center justify-center`}
                    style={{
                      color: textColor,
                    }}
                  >
                    <Typography
                      weight="bold"
                      htmlTag="p"
                      type="inherit"
                      clamp="clamp1"
                    >
                      {values.name}
                    </Typography>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

const VitalsTile = (props: VitalsTileProps) => {
  const { config, groups, encounters, view } = props;

  return (
    <div
      className="flex"
      style={{
        height: `${VitalsTileHeight}px`,
      }}
    >
      {groups?.map((item, i) => (
        <VitalGroup
          key={item + i}
          group={item}
          encounters={encounters}
          view={view}
          config={config}
          index={i}
          groups={groups}
        />
      ))}
    </div>
  );
};

export default VitalsTile;
